import React, { FC, useEffect, useState } from 'react'
import { styles } from './distributives-styles'
import { DistributivesProps, DistributivesStatus, GetPlatformResponse } from './distributives-types'
import { Distributive, Platform } from './models/platforms'
import { DistributivesHeader } from './organisms/DistributivesHeader'
import { api } from 'core/api/ConnectionManager'
import translate from 'core/translate'
import { PlatformCard } from './organisms/PlatformCard'
import { isNotEmptyArray } from 'core/utils/coreUtil'
import { ContentWithoutToolbar } from 'organisms/ContentWithoutToolbar'
import { checkAvailableModule } from '../../core/helpers/routes/routes'
import Checkbox from '../../blocks.simple/checkbox/checkbox'
import { cn } from 'ethcss'
import Icon from '../../blocks.simple/icon/icon'
import Button from '../../blocks.simple/button/button'
import AnimationWrapper from 'blocks.app/animationWrapper/animationWrapper'
import { isHorizontalMenu } from 'core/helpers/menu'
import { RootStateOrAny, useSelector } from 'react-redux'
import { number } from 'prop-types'

const DistributivesComponent: FC<DistributivesProps> = () => {
    const [platforms, setPlatforms] = useState<Platform[]>([])
    const [status, setStatus] = useState<DistributivesStatus>('idle')
    const [selectedItems, setSelectedItems] = useState<{ id: number; uuid?: string }[]>([])
    const [deleteConfirmList, setDeleteConformList] = useState<{ id: number; uuid?: string }[]>([])
    const [filter, setFilter] = useState('all')
    const filterList = ['all', 'ofUsers', 'general']
    const { isMobile }: any = useSelector<RootStateOrAny>((state) => state.app)

    async function getPlatforms() {
        try {
            let response = await api.send<object, GetPlatformResponse>('getPlatformsDistributives', {})

            setPlatforms(response)
            setStatus('success')
        } catch (err) {
            setPlatforms([])
            setStatus('error')
        }
    }

    const onSelect = (distributive: Distributive, value: boolean) => {
        setSelectedItems(
            value
                ? [...selectedItems, { id: distributive.id, uuid: distributive.uuid || '' }]
                : selectedItems.filter((item) => {
                      if (distributive.uuid) {
                          return item.uuid !== distributive.uuid
                      }

                      return item.id !== distributive.id
                  })
        )
    }

    const onDelete = (distributive: { id: number; uuid?: string }) => {
        setDeleteConformList([distributive])
    }

    const onDeleteAll = () => {
        setDeleteConformList(selectedItems)
    }

    const onDeleteConfirm = () => {
        deleteDistributives(deleteConfirmList)
        onCancelDelete()
    }

    const onCancelDelete = () => {
        setDeleteConformList([])
    }

    const deleteDistributives = (list: { id: number; uuid?: string }[]) => {
        if (!list.length) return

        api.send('deleteDistributives', {
            distributiveId: list.map((item) => {
                const data: { id: number; uuid?: string } = { id: item.id }

                if (item.uuid) {
                    data.uuid = item.uuid
                }

                return data
            }),
        })
    }

    const onSelectAll = (select: boolean) => {
        const selectedList: { id: number; uuid?: string }[] = []

        if (select) {
            platforms.forEach((platform: any) => {
                platform.lastClientApplications.forEach((app: any) => {
                    if (app.uploadedBy) {
                        const data: { id: number; uuid?: string } = { id: app.id }

                        if (app.uuid) {
                            data.uuid = app.uuid
                        }

                        selectedList.push(data)
                    }
                })
            })
        }

        setSelectedItems(selectedList)
    }

    useEffect(() => {
        getPlatforms()
    }, [])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'distributiveCreated',
            (data: any) => {
                setPlatforms(
                    platforms.map((platform: any) => {
                        if (platform.type === data.parentType) {
                            return {
                                ...platform,
                                lastClientApplications: [data, ...platform.lastClientApplications],
                            }
                        }

                        return platform
                    })
                )
            },
            listenersId
        )

        api.addObserver(
            'distributiveDeleted',
            (data: any) => {
                setPlatforms(
                    platforms.map((platform: any) => {
                        return {
                            ...platform,
                            lastClientApplications: platform.lastClientApplications.filter((app: any) => {
                                let distributive = null

                                if (app.uuid) {
                                    distributive = data.find((item: any) => item.uuid === app.uuid)
                                } else {
                                    distributive = data.find((item: any) => item.id === app.id)
                                }

                                return !distributive
                            }),
                        }
                    })
                )
            },
            listenersId
        )

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [platforms])

    const isError = () => status === 'error'

    const render = () => {
        if (isError()) return

        return renderPlatformCards()
    }

    const sortPlatformByOrder = (prevPlatform: Platform, nextPlatform: Platform) =>
        prevPlatform.order - nextPlatform.order

    const renderPlatformCards = () => {
        return (
            isNotEmptyArray(platforms) &&
            platforms.sort(sortPlatformByOrder).map((platform) => {
                const { title } = platform

                return (
                    <AnimationWrapper>
                        <div key={title} className={styles.distributives__platform}>
                            <PlatformCard
                                platform={platform}
                                selectedItems={selectedItems}
                                onSelect={onSelect}
                                onDelete={onDelete}
                                filter={filter}
                            />
                        </div>
                    </AnimationWrapper>
                )
            })
        )
    }

    const renderDeleteConfirm = () => {
        if (!deleteConfirmList.length) return <></>

        return (
            <div className={styles.confirmModal}>
                <div className={styles.confirmModalInner}>
                    <div className={styles.confirmModalCaption}>
                        {translate('deleteConfirm')}
                        {translate(deleteConfirmList.length > 1 ? '_files' : 'file').toLowerCase()}?
                    </div>
                    <div className={styles.buttons}>
                        <Button
                            mod={'withBorder'}
                            className={styles.button}
                            onClick={onCancelDelete}
                            textSize={'mini'}
                            indentSize={'mini'}
                            rounded={'full_normal'}
                        >
                            {translate('cancel')}
                        </Button>
                        <Button
                            className={styles.button}
                            mod={'fill'}
                            textSize={'mini'}
                            indentSize={'mini'}
                            rounded={'full_normal'}
                            onClick={onDeleteConfirm}
                        >
                            {translate('delete')}
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <ContentWithoutToolbar
            title={translate('distributives')}
            className={styles.distributives}
            wrapperClassName={
                isHorizontalMenu() || isMobile
                    ? styles.distributives__wrapper__withPadding
                    : styles.distributives__wrapper
            }
        >
            <DistributivesHeader onUpdate={getPlatforms} />
            <div className={styles.tabs}>
                {filterList.map((item) => (
                    <div
                        className={cn(styles.tab, filter === item ? styles.tabActive : '')}
                        onClick={() => setFilter(item)}
                    >
                        {translate(item)}
                    </div>
                ))}
            </div>
            <div className={styles.distributives__content}>
                <div className={styles.panel}>
                    <div className={styles.selectAll}>
                        <Checkbox checked={!!selectedItems.length} onClick={onSelectAll} />
                        <div className={styles.selectAllLabel}>
                            {translate(selectedItems.length ? 'deselect' : 'selectAll')}
                        </div>
                    </div>
                    <div className={styles.deleteAll}>
                        <Icon
                            type={'delete'}
                            onClick={onDeleteAll}
                            tooltip={{
                                html: translate('deleteAll'),
                            }}
                            disabled={!selectedItems.length}
                        />
                    </div>
                </div>
                {render()}
            </div>
            {renderDeleteConfirm()}
        </ContentWithoutToolbar>
    )
}

export const Distributives = checkAvailableModule('distributive') ? DistributivesComponent : null
